export type TournamentSectionKey = 'overview' | 'teams' | 'schedule' | 'results' | 'standings' | 'bracket' | 'classification' | 'registration' | 'setup';

export interface TournamentWorkspaceSection {
  key: TournamentSectionKey;
  tabs: string[];
}

/** The competition management API is restricted to tournament editors. */
export function canViewCompetitionWorkspace(access: { canEdit: boolean }): boolean {
  return access.canEdit;
}

/** Group only authorized tabs; existing tab URLs remain the source of truth. */
export function tournamentWorkspaceSections(
  availableKeys: readonly string[]
): TournamentWorkspaceSection[] {
  const available = new Set(availableKeys);
  const canConfigure = available.has('settings');
  const sections: TournamentWorkspaceSection[] = [
    { key: 'overview', tabs: ['info'] },
    { key: 'teams', tabs: ['teams'] },
    { key: 'schedule', tabs: ['schedule'] },
    { key: 'results', tabs: ['results'] },
    { key: 'standings', tabs: ['standings'] },
    { key: 'bracket', tabs: ['bracket'] },
    { key: 'classification', tabs: ['classification'] },
    { key: 'registration', tabs: canConfigure ? [] : ['registration'] },
    {
      key: 'setup',
      tabs: canConfigure ? ['general', 'manage-teams', 'registration', 'manage-competition', 'manage-classification', 'manage-schedule', 'scheduling', 'manage-categories', 'venues', 'settings'] : [],
    },
  ];
  return sections
    .map((section) => ({ ...section, tabs: section.tabs.filter((key) => available.has(key)) }))
    .filter((section) => section.tabs.length > 0);
}

export function resolveTournamentWorkspaceTab(
  availableKeys: readonly string[],
  requested: string
): string {
  if (availableKeys.includes(requested)) return requested;
  if (requested === 'categories' && availableKeys.includes('info')) return 'info';
  if (requested === 'competition' && availableKeys.includes('manage-competition')) return 'manage-competition';
  if (requested === 'groups' && availableKeys.includes('manage-competition')) return 'manage-competition';
  if (requested === 'scheduling' && availableKeys.includes('schedule')) return 'schedule';
  return availableKeys[0] ?? 'info';
}

export interface OverviewMatch {
  id: string;
  matchTime?: string | null;
  status?: string | null;
  teamA?: { name: string } | null;
  teamB?: { name: string } | null;
  category?: { name: string } | null;
  court?: { name: string } | null;
}

/** Preserve tournament navigation through SSO without retaining auth callback parameters. */
export function tournamentWorkspaceRedirectUri(location: { origin: string; pathname: string; search: string }): string | undefined {
  if (!/^\/tournaments\/[^/]+\/?$/.test(location.pathname)) return undefined;
  const redirect = new URL(location.pathname, location.origin);
  const search = new URLSearchParams(location.search);
  for (const key of ['tab', 'category']) {
    const value = search.get(key);
    if (value) redirect.searchParams.set(key, value);
  }
  return redirect.href;
}

export function upcomingTournamentMatches(matches: readonly OverviewMatch[]): OverviewMatch[] {
  return matches
    .filter((match) =>
      ['SCHEDULED', 'IN_PROGRESS', 'INPROGRESS', 'LIVE'].includes(
        (match.status ?? '').toUpperCase()
      )
    )
    .slice()
    .sort((a, b) => {
      const time = (value?: string | null) =>
        value && Number.isFinite(Date.parse(value)) ? Date.parse(value) : Infinity;
      return time(a.matchTime) - time(b.matchTime);
    })
    .slice(0, 3);
}
